import { Button, Card, Divider, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { dayjs } from '../../utils/dayjs';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { paymentReportsInitialFacilityHierarchy } from './shared';
import { reportingPanelStyles } from './styles';
import { TransactionReportFilter, type TransactionReportFilterValue } from './TransactionReportFilter';
import {
	initialTransactionTimeRange,
	TRANSACTION_REPORT_FACILITY_TIMEZONE,
	TransactionTimeRangeFilter,
	type TransactionTimeRangeValue,
} from './TransactionTimeRangeFilter';
import { buildTransactionReportParameters, type TransactionDateRangeValue } from './transactionReportParameters';
import type { ReportFormProps } from './types';

const initialDateRange: TransactionDateRangeValue = {
	startDate: null,
	endDate: null,
};

const initialFilter: TransactionReportFilterValue = {
	type: 'all',
};

export const TransactionReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onReset,
	onValidationError,
}: ReportFormProps) => {
	const [formKey, setFormKey] = useState(0);
	const [dateRange, setDateRange] = useState<TransactionDateRangeValue>(initialDateRange);
	const [timeRange, setTimeRange] = useState(() => initialTransactionTimeRange());
	const [filter, setFilter] = useState<TransactionReportFilterValue>(initialFilter);
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(
		paymentReportsInitialFacilityHierarchy,
	);

	const canSubmit = useMemo(() => Boolean(dateRange.startDate), [dateRange.startDate]);

	const reset = () => {
		setDateRange(initialDateRange);
		setTimeRange(initialTransactionTimeRange());
		setFilter(initialFilter);
		setFacilityHierarchy(paymentReportsInitialFacilityHierarchy);
		setFormKey((current) => current + 1);
		onReset?.();
	};

	const validateTimeRange = () => {
		if (!timeRange.startTime || !timeRange.endTime || !dateRange.startDate) {
			return true;
		}

		const endDate = dateRange.endDate ?? dayjs();
		const startKey = `${dateRange.startDate.format('YYYY-MM-DD')} ${timeRange.startTime.format('HH:mm')}`;
		const endKey = `${endDate.format('YYYY-MM-DD')} ${timeRange.endTime.format('HH:mm')}`;

		if (timeRange.timezone !== TRANSACTION_REPORT_FACILITY_TIMEZONE) {
			const startDateTime = dayjs.tz(startKey, 'YYYY-MM-DD HH:mm', timeRange.timezone);
			const endDateTime = dayjs.tz(endKey, 'YYYY-MM-DD HH:mm', timeRange.timezone);

			if (startDateTime.isAfter(endDateTime)) {
				onValidationError('Start date and time must be on or before end date and time.');
				return false;
			}

			return true;
		}

		if (startKey > endKey) {
			onValidationError('Start date and time must be on or before end date and time.');
			return false;
		}

		return true;
	};

	const validate = () => {
		if (!dateRange.startDate) {
			onValidationError('Select a start date.');
			return false;
		}

		if (dateRange.endDate?.isBefore(dateRange.startDate, 'day')) {
			onValidationError('End date cannot be before the start date.');
			return false;
		}

		if (!validateTimeRange()) {
			return false;
		}

		if (filter.type === 'accountId' && !filter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (filter.type === 'subscriptionName' && !filter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		if (filter.type === 'invoiceId' && !filter.value.trim()) {
			onValidationError('Enter an invoice ID.');
			return false;
		}

		if (filter.type === 'creditId' && !filter.value.trim()) {
			onValidationError('Enter a credit ID.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		const parameters = buildTransactionReportParameters({
			dateRange,
			filter,
			facilityHierarchy,
			timeRange,
		});

		await createReport({
			reportType: 'transaction',
			parameters,
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<LocalizationProvider dateAdapter={AdapterDayjs} key={formKey}>
					<TransactionTimeRangeFilter
						value={timeRange}
						onChange={setTimeRange}
						startDateField={
							<DatePicker<Dayjs>
								label='Start date *'
								value={dateRange.startDate}
								onChange={(startDate) => {
									setDateRange({
										startDate,
										endDate: dateRange.endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						}
						endDateField={
							<DatePicker<Dayjs>
								label='End date'
								value={dateRange.endDate}
								onChange={(endDate) => {
									setDateRange({
										startDate: dateRange.startDate,
										endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						}
					/>
				</LocalizationProvider>

				<Divider />

				<TransactionReportFilter filter={filter} onChange={setFilter} />

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
